import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

/**
 * Click-or-drop image uploader. Used inside component editors so an
 * owner can swap an image inline without leaving the section.
 */
export default function ImageDropUploader({ brandId, currentSrc, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  /**
   * Whether the stored address actually loads.
   *
   * An <img> with an empty alt that fails to load draws nothing at all — no
   * icon, no box, no space. So a picture saved under an address that has
   * since stopped working looked exactly like no picture, except that
   * "Remove this picture" was sitting underneath it. There was nothing on
   * screen to tell the two apart, and nothing to report.
   */
  const [broken, setBroken] = useState(false);
  const inputRef = useRef(null);

  // A new address deserves its own chance to load.
  useEffect(() => { setBroken(false); }, [currentSrc]);

  const doUpload = async (file) => {
    if (!file) return;
    if (!brandId) {
      toast.error('Select a brand first');
      return;
    }
    if (!file.type?.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('brand_id', brandId);
      fd.append('file', file);
      const res = await API.uploadSiteAsset(fd);
      if (res?.url) {
        onUploaded(res.url);
        toast.success('Image uploaded');
      } else {
        toast.error('Upload returned no URL');
      }
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) doUpload(file);
      }}
      sx={{
        bgcolor: dragOver ? 'action.selected' : 'action.hover',
        border: '1px dashed',
        borderColor: dragOver ? 'primary.main' : 'divider',
        borderRadius: '8px',
        p: '10px',
        textAlign: 'center',
        cursor: uploading ? 'wait' : 'pointer',
        transition: 'background-color 120ms ease, border-color 120ms ease',
      }}
    >
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) doUpload(file);
          e.target.value = '';
        }}
      />
      {currentSrc ? (
        <Box>
          {broken ? (
            <Box sx={{ py: '8px' }}>
              <Typography variant='caption' color='error' display='block' fontWeight='700'>
                This picture could not be loaded
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
                sx={{ wordBreak: 'break-all', mt: '2px' }}
              >
                {currentSrc}
              </Typography>
            </Box>
          ) : (
            <img
              src={currentSrc}
              alt='What is currently saved here'
              onError={() => setBroken(true)}
              style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 6, display: 'inline-block' }}
            />
          )}
          <Typography variant='caption' color='text.secondary' mt='4px' display='block'>
            {uploading
              ? 'Uploading…'
              : broken
                ? 'Click or drop an image to replace it'
                : 'Click or drop a new image to replace'}
          </Typography>
        </Box>
      ) : (
        <Typography variant='caption' color='text.secondary'>
          {uploading ? 'Uploading…' : 'Click or drop an image here'}
        </Typography>
      )}
    </Box>
  );
}
